/**
 * 던전 전투 흐름 (일감 S3) — GameScene에서 추출한 셸 오케스트레이션.
 * 도전 진입(전장 전환+제한시간) → 클리어(보상+도전권 소모+복원) / 실패(무소모 복원) / 소탕.
 * 결정(canChallenge/sweep/보상 인덱스)은 DungeonController·RewardService가, 여기는 타이밍·전환만.
 * 상태(mode/battleIndex/deadline)는 비저장 런타임 — scene.restart 시 재생성으로 초기화.
 */
import type Phaser from "phaser";
import { t } from "../i18n/i18n.js";
import { MODULE_BRICK_BALANCE } from "../data/balance.js";
import type { GameContext } from "../app/GameContext.js";
import type { ShellPorts } from "../app/commands/ports.js";
import type { DungeonTimerBar } from "../ui/DungeonTimerBar.js";

export interface DungeonFlowDeps {
  scene: Phaser.Scene;
  ctx: GameContext;
  ports: ShellPorts;
  battlefield: {
    clearField(): void;
    buildDungeonGrid(index: number): void;
    buildChapterGrid(): void;
  };
  /** 게임뷰 상단 제한시간 bar — buildUi에서 생성되므로 getter. */
  timerBar: () => DungeonTimerBar;
  setActiveTab: (tab: "battle" | "dungeon") => void;
  showCenterToast: (message: string) => void;
  /** index 보상 해석·지급 + 획득 모달 — 모달 흐름이 셸 잔존이라 콜백 (S6에서 재결선). */
  grantDungeonReward: (index: number) => void;
  /** 던전 기능 성공 알림 — 가이드퀘스트 actionCompleted 목표 진행. */
  onActionCompleted?: (target: string) => void;
}

export class DungeonFlow {
  private mode = false;
  private battleIndex = 0;
  private deadlineMs = 0;
  private pausedAtMs: number | null = null;

  constructor(private readonly deps: DungeonFlowDeps) {}

  /** 던전 전투 중 여부 — 전멸 분기/타이머 tick/도전 가능 판정에 사용. */
  isActive(): boolean {
    return this.mode;
  }

  setPaused(paused: boolean): void {
    if (!this.mode) {
      this.pausedAtMs = null;
      return;
    }
    if (paused) {
      if (this.pausedAtMs === null) this.pausedAtMs = this.deps.scene.time.now;
      return;
    }
    if (this.pausedAtMs !== null) {
      this.deadlineMs += this.deps.scene.time.now - this.pausedAtMs;
      this.pausedAtMs = null;
    }
  }

  /** 도전 — 던전 전투 시작 (도전권은 클리어 시 소모·실패 시 무소모). 전투 필드를 던전 벽돌로 전환 + 제한시간 시작. */
  challenge(): void {
    const { ctx, ports, battlefield } = this.deps;
    if (this.mode || !ctx.dungeon().canChallenge()) return;
    this.mode = true;
    this.battleIndex = ctx.dungeon().currentIndex;
    this.deadlineMs =
      this.deps.scene.time.now + MODULE_BRICK_BALANCE.dungeonTimeLimitSec * 1000;
    battlefield.clearField();
    battlefield.buildDungeonGrid(this.battleIndex);
    this.deps.timerBar().setVisible(true);
    ctx.soundController().play("box");
    this.deps.setActiveTab("battle"); // 전투 화면 표시 — 자동 발사로 던전 벽돌 격파
    this.deps.onActionCompleted?.("dungeon.challenge");
    ports.invalidateUi();
    ports.persist();
  }

  /** 소탕 — 도전권 1 소모 후 마지막 클리어 던전 보상 즉시 수령 (전투 없음). */
  sweep(): void {
    const { ctx, ports } = this.deps;
    if (this.mode || !ctx.dungeon().sweep()) return;
    this.deps.grantDungeonReward(ctx.dungeon().clearedMaxIndex);
    ctx.soundController().play("box");
    ports.invalidateUi();
    ports.persistNow(); // 소탕 보상 수령(도전권 소모) — 즉시 저장
  }

  /** 던전 클리어 — 보상 지급 + 인덱스 +1 + 일반 챕터 복원 (던전 전투 마지막 벽돌 파괴 시). */
  onFieldCleared(): void {
    const { ctx, ports, battlefield } = this.deps;
    this.deps.grantDungeonReward(this.battleIndex);
    this.deps.onActionCompleted?.("dungeon.clear");
    ctx.dungeon().consumeTicket(); // 보상 수령 시점에 도전권 소모 (실패 시 무소모)
    ctx.dungeon().markCleared();
    this.mode = false;
    this.deps.timerBar().setVisible(false);
    ctx.soundController().play("rebirth");
    // damageBrick 재귀(explosion/chain 라이브 순회) 완전 종료 후 다음 틱에 그리드 전환 —
    // 재귀 중 clear+buildChapterGrid가 새 일반 벽돌을 생성해 폭발 라이브 순회에 오염되는 것 방지.
    this.deps.scene.time.delayedCall(0, () => {
      battlefield.clearField();
      battlefield.buildChapterGrid(); // 일반 챕터 복원
      this.deps.setActiveTab("dungeon");
      ports.invalidateUi();
    });
    ports.persistNow(); // 던전 클리어 보상 수령 + 도전권 소모 — 즉시 저장
  }

  /** 던전 전투 제한시간 갱신 (update 매 프레임, isActive일 때만) — bar 갱신 + 시간 초과 시 실패 처리. */
  tick(): void {
    if (this.pausedAtMs !== null) return;
    const total = MODULE_BRICK_BALANCE.dungeonTimeLimitSec * 1000;
    const remaining = this.deadlineMs - this.deps.scene.time.now;
    this.deps.timerBar().update(remaining, total);
    if (remaining <= 0) this.fail();
  }

  /** 던전 실패 — 제한시간 초과. 토스트 + 도전권 무소모 + 일반 챕터 복귀(인덱스·보상 없음). */
  private fail(): void {
    const { ctx, ports, battlefield } = this.deps;
    this.mode = false;
    this.deps.timerBar().setVisible(false);
    this.deps.showCenterToast(t("dungeon.failed"));
    ctx.soundController().play("hit");
    this.deps.scene.time.delayedCall(0, () => {
      battlefield.clearField();
      battlefield.buildChapterGrid(); // 일반 챕터 복원
      this.deps.setActiveTab("dungeon");
      ports.invalidateUi();
    });
    ports.persist();
  }
}
